/**
 * F-017 — Game-over detector (BR-004).
 *
 * Watches whether at least one planet body is above the playfield's
 * limit line. The "above" state has to persist for `holdMs` (default
 * 1500ms) before triggering, so a transient bounce that crosses the
 * line briefly does NOT end the game.
 *
 * Stateless inputs each tick, internal timer state. Reset on game over
 * fire so the same instance can be reused across runs.
 */

import type { Planet } from "./planet.ts";

const DEFAULT_HOLD_MS = 1500;

export interface GameOverDetector {
  /** Tick the detector with current planet snapshot + simulation time (ms). */
  evaluate(planets: ReadonlyArray<Planet>, nowMs: number): boolean;
  /** Time the limit-line breach has been held (clamped to holdMs). */
  breachedForMs(nowMs: number): number;
  reset(): void;
}

export interface GameOverDetectorOptions {
  limitLineY: number;
  holdMs?: number;
  onGameOver?: () => void;
}

export function createGameOverDetector(
  opts: GameOverDetectorOptions,
): GameOverDetector {
  const holdMs = opts.holdMs ?? DEFAULT_HOLD_MS;
  let breachStartMs: number | null = null;
  let fired = false;

  const evaluate = (planets: ReadonlyArray<Planet>, nowMs: number): boolean => {
    if (fired) return true;

    const breached = planets.some((p) => p.body.position.y > opts.limitLineY);

    if (!breached) {
      breachStartMs = null;
      return false;
    }

    if (breachStartMs === null) {
      breachStartMs = nowMs;
      return false;
    }

    if (nowMs - breachStartMs >= holdMs) {
      fired = true;
      opts.onGameOver?.();
      return true;
    }
    return false;
  };

  const breachedForMs = (nowMs: number): number => {
    if (breachStartMs === null) return 0;
    return Math.min(nowMs - breachStartMs, holdMs);
  };

  const reset = (): void => {
    breachStartMs = null;
    fired = false;
  };

  return { evaluate, breachedForMs, reset };
}

export const __testing = { DEFAULT_HOLD_MS };
